import {
    AccessTimeOutlined,
    DashboardOutlined,
    DescriptionOutlined,
    DirectionsOutlined,
    EventOutlined,
    LanguageOutlined,
    LayersOutlined,
    LocalHospitalOutlined,
    LocationOnOutlined,
    LogoutOutlined,
    ManageAccounts,
    MapOutlined,
    MonitorOutlined,
    Person2Outlined,
    QueryStats,
    RouteOutlined,
    SettingsOutlined,
} from '@mui/icons-material';
import { Divider, List } from '@mui/material';
import { Fragment } from 'react';

import { useLogout, useUserEmailIfLoggedIn } from '../../../auth/authentication';
import { useAppConfig } from '../../../fetching/api/appConfig';
import { FeatureFlag } from '../../../types/featureFlag';
import { ReturnComponent } from '../../../utils/reactTypes';
import { useFeatureFlagEnabled } from '../../../utils/useFeatureFlagEnabled';
import { DrawerItemConfig } from '../lib/types';
import { GlobalDrawerHeader } from './GlobalDrawerHeader';
import { GlobalDrawerItem } from './GlobalDrawerItem';

export const GlobalDrawerItems = (): ReturnComponent => {
    const appConfig = useAppConfig();
    const hasDashboardFeature = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_HOME_VIEW);
    const hasPatientIntakeCapacityPage = useFeatureFlagEnabled(
        FeatureFlag.PATIENT_INTAKE_CAPACITY_PAGE,
    );

    const currentUserEmail = useUserEmailIfLoggedIn();

    const logout = useLogout();

    const DRAWER_ITEMS: DrawerItemConfig[] = [
        {
            label: 'Dashboard',
            path: '/',
            exact: true,
            icon: <MonitorOutlined />,
            canAccess: hasDashboardFeature,
        },
        {
            label: 'Patients',
            path: '/patients',
            icon: <Person2Outlined />,
            canAccess: appConfig.patient.page,
        },
        {
            label: 'Visits',
            path: '/visits',
            icon: <LanguageOutlined />,
            canAccess: appConfig.visit.page,
        },
        {
            label: 'Clinicians',
            path: '/clinicians',
            icon: <LocalHospitalOutlined />,
            canAccess: appConfig.qhp.page,
        },
        {
            label: 'Reports',
            path: '/reports',
            icon: <DescriptionOutlined />,
            canAccess: appConfig.reports.page,
        },
        {
            label: 'Insights',
            path: '/insights',
            icon: <QueryStats />,
            canAccess: appConfig.insights.page,
        },
        {
            label: 'Route Optimizer',
            path: '/route-optimizer',
            exact: true,
            icon: <RouteOutlined />,
            canAccess: appConfig.route_optimizer.page,
        },
        {
            label: 'Route Optimizer Admin',
            path: '/route-optimizer/admin',
            icon: <DirectionsOutlined />,
            canAccess: appConfig.route_optimizer.preview_clinician_view,
        },
        {
            label: 'Services',
            path: '/services',
            icon: <LayersOutlined />,
            canAccess: appConfig.service.page,
            isDivider: true,
        },
        {
            label: 'Clinician Schedules',
            path: '/schedules',
            icon: <AccessTimeOutlined />,
            canAccess: appConfig.shift.page,
        },
        {
            label: 'Territories',
            path: '/territories',
            icon: <MapOutlined />,
            canAccess: appConfig.territories.page,
        },
        {
            label: 'Service Areas',
            path: '/service-areas',
            icon: <LocationOnOutlined />,
            canAccess: appConfig.service_area.page,
        },
        {
            label: 'Operating Hour Blocks',
            path: '/blocks',
            icon: <EventOutlined />,
            canAccess: appConfig.operating_hour_block.page,
        },
        {
            label: 'User Settings',
            path: '/user-settings',
            icon: <ManageAccounts />,
            canAccess: appConfig.user_settings.page,
            isDivider: true,
        },
        {
            label: 'Settings',
            path: '/settings',
            icon: <SettingsOutlined />,
            canAccess: appConfig.settings.page,
        },
        {
            label: 'Capacity Check',
            path: '/patient-intake',
            icon: <DashboardOutlined />,
            canAccess: hasPatientIntakeCapacityPage,
            isDivider: true,
        },
    ];

    return (
        <>
            <GlobalDrawerHeader />
            <List
                component={'nav'}
                sx={{
                    bgcolor: 'transparent',
                }}
            >
                {DRAWER_ITEMS.filter((item) => item.canAccess).map((item) => (
                    <Fragment key={item.label}>
                        {item.isDivider && <Divider sx={{ mx: 3, my: 1 }} />}
                        <GlobalDrawerItem item={item} />
                    </Fragment>
                ))}
                <Divider sx={{ mx: 3, my: 1 }} />
                <GlobalDrawerItem
                    item={{
                        label: 'Logout',
                        subLabel: currentUserEmail ?? undefined,
                        icon: <LogoutOutlined />,
                        canAccess: true,
                        onClick: logout,
                    }}
                />
            </List>
        </>
    );
};
